import { useState } from "react";
import { Receipt, FilePlus2, Loader2, Banknote, CreditCard, Undo2, Mail, RefreshCw, Trash2, Pencil, CheckCircle2, Building2, Search, Copy } from "lucide-react";
import { apiFetch, openPdf } from "@/portal/api";
import { fmtCZK, invoiceStatus } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import DateInput from "@/portal/components/DateInput";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const methodLabel = (m) => (m === "cash" ? "hotově" : m === "transfer" ? "převodem" : null);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("cs-CZ") : "");

const TYPE_LABEL = { rent: "nájemné", deposit: "kauce", fine: "pokuta", service: "servis", insurance: "pojištění", other: "ostatní" };
const CUSTOM_CATEGORIES = [
  ["fine", "Pokuta"],
  ["service", "Servis"],
  ["insurance", "Pojištění"],
  ["other", "Jiné"],
];

// Inline form for an ad-hoc invoice (fine, service, insurance, anything).
const CustomInvoiceForm = ({ client, token, onChange, onClose }) => {
  const [f, setF] = useState({ category: "fine", description: "", amount: "", due_on: "", rental_id: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.description.trim()) { toast.error("Zadejte popis položky."); return; }
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Zadejte kladnou částku."); return; }
    if (!f.due_on) { toast.error("Vyberte datum splatnosti."); return; }
    setSaving(true);
    try {
      await apiFetch(`/admin/clients/${client.id}/invoices/custom`, {
        method: "POST", token,
        body: {
          category: f.category,
          description: f.description.trim(),
          amount: Number(f.amount),
          due_on: f.due_on,
          rental_id: f.rental_id || null,
        },
      });
      toast.success("Faktura vystavena.");
      onChange();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 mb-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2" data-testid={`custom-invoice-form-${client.id}`}>
      <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5">
        <FilePlus2 className="w-3.5 h-3.5" /> Vlastní faktura (pokuta, servis, pojištění…)
      </p>
      <div className="flex flex-wrap gap-1.5" data-testid="custom-invoice-categories">
        {CUSTOM_CATEGORIES.map(([val, lbl]) => (
          <button key={val} type="button" onClick={() => setF({ ...f, category: val })}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${f.category === val ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-zinc-400 hover:text-white"}`}
            data-testid={`custom-invoice-cat-${val}`}>{lbl}</button>
        ))}
      </div>
      <input className="input-dark text-xs" placeholder="Popis položky (např. Pokuta – parkování Praha 5, 3. 6. 2026)"
        value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="custom-invoice-description" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="1" className="input-dark text-xs" placeholder="Částka (Kč)"
          value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="custom-invoice-amount" />
        <DateInput value={f.due_on} onChange={(v) => setF({ ...f, due_on: v })} placeholder="Splatnost" className="text-xs"
          title="Datum splatnosti" testid="custom-invoice-due" />
      </div>
      {(client.rentals || []).length > 0 && (
        <select className="input-dark text-xs" value={f.rental_id} onChange={(e) => setF({ ...f, rental_id: e.target.value })} data-testid="custom-invoice-rental">
          <option value="">Bez vazby na vozidlo</option>
          {client.rentals.map((r) => (
            <option key={r.id} value={r.id}>
              {r.car ? `${r.car.brand} ${r.car.model}` : "Vozidlo"}{r.license_plate ? ` (${r.license_plate})` : ""}
            </option>
          ))}
        </select>
      )}
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full bg-emerald-500 text-black font-semibold hover:bg-emerald-400 disabled:opacity-60" data-testid="custom-invoice-submit">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />} Vystavit fakturu
        </button>
        <button onClick={onClose} className="text-xs px-4 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5" data-testid="custom-invoice-cancel">Zrušit</button>
      </div>
    </div>
  );
};

// Inline edit of an already issued invoice (description, qty, unit price, due date).
const EditInvoiceForm = ({ invoice, token, onChange, onClose }) => {
  const cust = invoice.customer || {};
  const [f, setF] = useState({
    description: invoice.description || "",
    qty: invoice.qty ?? 1,
    unit_price: invoice.unit_price ?? invoice.amount ?? "",
    due_on: invoice.due_on || "",
    customer_name: cust.name || "",
    customer_ico: cust.ico || "",
    customer_dic: cust.dic || "",
    customer_address: cust.address || "",
    customer_email: cust.email || "",
  });
  const [saving, setSaving] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);

  const total = Number(f.qty || 0) * Number(f.unit_price || 0);

  const lookupAres = async () => {
    const ico = (f.customer_ico || "").replace(/\D/g, "");
    if (ico.length !== 8) { toast.error("Zadejte platné IČO (8 číslic)."); return; }
    setAresLoading(true);
    try {
      const d = await apiFetch(`/admin/ares/${ico}`, { token });
      setF((p) => ({
        ...p,
        customer_name: d.name || p.customer_name,
        customer_dic: d.dic || p.customer_dic,
        customer_address: d.address || p.customer_address,
      }));
      toast.success("Údaje odběratele doplněny z ARES.");
    } catch (e) { toast.error(e.message || "ARES lookup selhal."); }
    finally { setAresLoading(false); }
  };

  const save = async () => {
    if (!f.description.trim()) { toast.error("Popis položky nesmí být prázdný."); return; }
    if (!f.qty || Number(f.qty) <= 0) { toast.error("Počet (množství) musí být kladný."); return; }
    if (!f.unit_price || Number(f.unit_price) <= 0) { toast.error("Cena za jednotku musí být kladná."); return; }
    if (!f.customer_name.trim()) { toast.error("Jméno / název odběratele nesmí být prázdné."); return; }
    setSaving(true);
    try {
      await apiFetch(`/admin/invoices/${invoice.id}`, {
        method: "PUT", token,
        body: {
          description: f.description.trim(),
          qty: Number(f.qty),
          unit_price: Number(f.unit_price),
          due_on: f.due_on || null,
          customer_name: f.customer_name,
          customer_ico: f.customer_ico,
          customer_dic: f.customer_dic,
          customer_address: f.customer_address,
          customer_email: f.customer_email,
        },
      });
      toast.success("Faktura upravena.");
      onChange();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="w-full mt-1 mb-1 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-3 space-y-2" data-testid={`edit-invoice-form-${invoice.id}`}>
      <p className="text-[11px] uppercase tracking-wider text-indigo-300 font-semibold flex items-center gap-1.5">
        <Pencil className="w-3 h-3" /> Úprava faktury {invoice.number}
      </p>
      <input className="input-dark text-xs w-full" placeholder="Popis položky na faktuře"
        value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`edit-invoice-description-${invoice.id}`} />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[10px] text-zinc-500 mb-0.5">Počet (měs./ks)</label>
          <input type="number" min="1" className="input-dark text-xs w-full" value={f.qty}
            onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid={`edit-invoice-qty-${invoice.id}`} />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 mb-0.5">Cena/jedn. (Kč)</label>
          <input type="number" min="1" className="input-dark text-xs w-full" value={f.unit_price}
            onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid={`edit-invoice-price-${invoice.id}`} />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 mb-0.5">Splatnost</label>
          <DateInput value={f.due_on} onChange={(v) => setF({ ...f, due_on: v })} className="text-xs w-full" testid={`edit-invoice-due-${invoice.id}`} />
        </div>
      </div>
      {total > 0 && (
        <p className="text-[11px] text-indigo-300/90" data-testid={`edit-invoice-total-${invoice.id}`}>
          Nová celková částka: {total.toLocaleString("cs-CZ")} Kč ({f.qty}× {Number(f.unit_price).toLocaleString("cs-CZ")} Kč)
        </p>
      )}
      <div className="border-t border-white/10 pt-2 space-y-2">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> Odběratel na faktuře
        </p>
        <input className="input-dark text-xs w-full" placeholder="Jméno / název firmy odběratele"
          value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid={`edit-invoice-customer-name-${invoice.id}`} />
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
          <input className="input-dark text-xs w-full" placeholder="IČO"
            value={f.customer_ico} onChange={(e) => setF({ ...f, customer_ico: e.target.value })} data-testid={`edit-invoice-customer-ico-${invoice.id}`} />
          <button type="button" onClick={lookupAres} disabled={aresLoading} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-60 whitespace-nowrap" title="Doplnit název, DIČ a adresu podle IČO z ARES" data-testid={`edit-invoice-ares-${invoice.id}`}>
            {aresLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} ARES
          </button>
          <input className="input-dark text-xs w-full" placeholder="DIČ"
            value={f.customer_dic} onChange={(e) => setF({ ...f, customer_dic: e.target.value })} data-testid={`edit-invoice-customer-dic-${invoice.id}`} />
        </div>
        <input className="input-dark text-xs w-full" placeholder="Adresa / sídlo"
          value={f.customer_address} onChange={(e) => setF({ ...f, customer_address: e.target.value })} data-testid={`edit-invoice-customer-address-${invoice.id}`} />
        <input type="email" className="input-dark text-xs w-full" placeholder="E-mail odběratele (na faktuře)"
          value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} data-testid={`edit-invoice-customer-email-${invoice.id}`} />
      </div>
      <p className="text-[11px] text-zinc-600">Změny (včetně odběratele) se propíšou do PDF faktury. Poté ji znovu odešlete tlačítkem s obálkou.</p>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full bg-indigo-500 text-white font-semibold hover:bg-indigo-600 disabled:opacity-60" data-testid={`edit-invoice-save-${invoice.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Uložit změny
        </button>
        <button onClick={onClose} className="text-xs px-4 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5" data-testid={`edit-invoice-cancel-${invoice.id}`}>Zrušit</button>
      </div>
    </div>
  );
};

const InvoiceSection = ({ client, token, onChange }) => {
  const [busy, setBusy] = useState(null);
  const [months, setMonths] = useState({});
  const [due, setDue] = useState({});
  const [customPrice, setCustomPrice] = useState({});
  const [showCustom, setShowCustom] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const issue = async (rentalId, type) => {
    const dueOn = due[rentalId];
    if (!dueOn) { toast.error("Vyberte prosím datum splatnosti."); return; }
    setBusy(rentalId + type);
    try {
      const m = type === "rent" ? Number(months[rentalId] || 1) : 1;
      const up = type === "rent" && customPrice[rentalId] ? Number(customPrice[rentalId]) : null;
      await apiFetch(`/admin/rentals/${rentalId}/invoice`, { method: "POST", token, body: { type, months: m, due_on: dueOn, unit_price: up } });
      toast.success("Faktura vystavena.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const refresh = async (id) => {
    setBusy("r" + id);
    try {
      await apiFetch(`/admin/invoices/${id}/refresh`, { method: "POST", token });
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    const ok = await confirmDialog({ title: "Smazat tuto fakturu?", confirmText: "Smazat", danger: true });
    if (!ok) return;
    setBusy("d" + id);
    try {
      await apiFetch(`/admin/invoices/${id}`, { method: "DELETE", token });
      toast.success("Faktura smazána.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const duplicate = async (id) => {
    setBusy("c" + id);
    try {
      const inv = await apiFetch(`/admin/invoices/${id}/duplicate`, { method: "POST", token });
      toast.success(`Kopie vytvořena – faktura ${inv.number}. Upravte údaje a odešlete.`);
      onChange();
      setEditingId(inv.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async (id) => {
    setBusy("e" + id);
    try {
      const res = await apiFetch(`/admin/invoices/${id}/email`, { method: "POST", token });
      if (res.email_sent) toast.success("Faktura odeslána e-mailem.");
      else toast.error("E-mail se nepodařilo odeslat (zkontrolujte adresu klienta).");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const markPaid = async (id, method) => {
    setBusy("p" + id);
    try {
      await apiFetch(`/admin/invoices/${id}/mark-paid`, { method: "POST", token, body: { method } });
      toast.success("Faktura označena jako zaplacená.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const markUnpaid = async (id) => {
    const ok = await confirmDialog({ title: "Vrátit fakturu na NEZAPLACENO?", confirmText: "Vrátit" });
    if (!ok) return;
    setBusy("u" + id);
    try {
      await apiFetch(`/admin/invoices/${id}/mark-unpaid`, { method: "POST", token });
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`invoice-section-${client.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-zinc-500 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Fakturace</p>
        <button
          onClick={() => setShowCustom(!showCustom)}
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
          data-testid={`custom-invoice-toggle-${client.id}`}
        >
          <FilePlus2 className="w-3 h-3" /> Vlastní faktura
        </button>
      </div>
      {showCustom && (
        <CustomInvoiceForm client={client} token={token} onChange={onChange} onClose={() => setShowCustom(false)} />
      )}
      {client.rentals.map((r) => {
        const m = Number(months[r.id] || 1);
        const unit = customPrice[r.id] !== undefined && customPrice[r.id] !== "" ? Number(customPrice[r.id]) : Number(r.amount || 0);
        const total = m > 0 && unit > 0 ? m * unit : 0;
        return (
        <div key={r.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5 mb-2">
          <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-400">
            {r.car ? `${r.car.brand} ${r.car.model}` : ""}{r.license_plate ? ` (${r.license_plate})` : ""}:
          </span>
          <div className="inline-flex items-center gap-1" data-testid={`invoice-presets-${r.id}`}>
            {[[1, "1 měs."], [3, "3 měs."], [6, "6 měs."], [12, "Rok dopředu"]].map(([val, lbl]) => (
              <button key={val} type="button" onClick={() => setMonths({ ...months, [r.id]: String(val) })}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${m === val ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-zinc-400 hover:text-white"}`}
                data-testid={`invoice-preset-${val}-${r.id}`}>{lbl}</button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-zinc-400">
            <input
              type="number" min="1" max="24"
              value={months[r.id] || "1"}
              onChange={(e) => setMonths({ ...months, [r.id]: e.target.value })}
              className="input-dark w-14 py-1 text-center"
              title="Počet měsíců"
              data-testid={`invoice-months-${r.id}`}
            />
            <span>měs.</span>
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-zinc-400">
            <span>cena/měs</span>
            <input
              type="number" min="1"
              value={customPrice[r.id] ?? ""}
              onChange={(e) => setCustomPrice({ ...customPrice, [r.id]: e.target.value })}
              placeholder={r.amount ? String(r.amount) : "—"}
              className="input-dark w-24 py-1 text-center"
              title="Vlastní cena za měsíc (prázdné = dle smlouvy). Hodí se pro zvýhodněnou roční platbu."
              data-testid={`invoice-unit-price-${r.id}`}
            />
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-zinc-400">
            <span>splatnost</span>
            <DateInput
              value={due[r.id] || ""}
              onChange={(v) => setDue({ ...due, [r.id]: v })}
              placeholder="Vyberte"
              className="py-1"
              title="Datum splatnosti"
              testid={`invoice-due-${r.id}`}
            />
          </div>
          <button
            onClick={() => issue(r.id, "rent")}
            disabled={busy === r.id + "rent" || (!r.amount && !customPrice[r.id])}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors disabled:opacity-40"
            data-testid={`invoice-issue-rent-${r.id}`}
          >
            {busy === r.id + "rent" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FilePlus2 className="w-3 h-3" />} Faktura nájemné
          </button>
          <button
            onClick={() => issue(r.id, "deposit")}
            disabled={busy === r.id + "deposit" || !r.deposit}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors disabled:opacity-40"
            data-testid={`invoice-issue-deposit-${r.id}`}
          >
            {busy === r.id + "deposit" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FilePlus2 className="w-3 h-3" />} Faktura kauce
          </button>
          </div>
          {total > 0 && m > 1 && (
            <p className="text-[11px] text-emerald-400/90 mt-1.5" data-testid={`invoice-total-preview-${r.id}`}>
              Faktura bude na {total.toLocaleString("cs-CZ")} Kč ({m}× {unit.toLocaleString("cs-CZ")} Kč{m === 12 ? " – platba na rok dopředu" : ""})
            </p>
          )}
        </div>
        );
      })}

      {client.invoices && client.invoices.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {client.invoices.map((inv) => {
            const st = invoiceStatus(inv.status);
            return (
              <div key={inv.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`invoice-row-${inv.id}`}>
                <button
                  onClick={() => openPdf(`/admin/invoices/${inv.id}/pdf`, token).catch((e) => toast.error(e.message))}
                  className="flex items-center gap-2 text-zinc-300 hover:text-white text-left min-w-0"
                  data-testid={`invoice-pdf-${inv.id}`}
                >
                  <Receipt className="w-3.5 h-3.5 text-emerald-400" />
                  Faktura {inv.number} · {TYPE_LABEL[inv.invoice_type] || "nájemné"} · {fmtCZK(inv.amount)}
                </button>
                <span className={`px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                {inv.status === "paid" ? (
                  <>
                    {methodLabel(inv.payment_method) && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-300 border border-green-500/20" data-testid={`invoice-method-${inv.id}`}>
                        {inv.payment_method === "cash" ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                        {methodLabel(inv.payment_method)}{inv.paid_on ? ` · ${fmtDate(inv.paid_on)}` : ""}
                      </span>
                    )}
                    <button onClick={() => markUnpaid(inv.id)} disabled={busy === "u" + inv.id} className="text-zinc-500 hover:text-amber-400 p-1" title="Vrátit na nezaplaceno" data-testid={`invoice-mark-unpaid-${inv.id}`}>
                      {busy === "u" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                    </button>
                  </>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button disabled={busy === "p" + inv.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50" title="Označit jako zaplaceno – vyberte způsob úhrady" data-testid={`invoice-mark-paid-${inv.id}`}>
                        {busy === "p" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Banknote className="w-3 h-3" />} Zaplaceno
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-zinc-900 border border-white/10 text-zinc-200">
                      <DropdownMenuItem onClick={() => markPaid(inv.id, "cash")} className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" data-testid={`invoice-paid-cash-${inv.id}`}>
                        <Banknote className="w-3.5 h-3.5 mr-2 text-green-400" /> Hotově
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => markPaid(inv.id, "transfer")} className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" data-testid={`invoice-paid-transfer-${inv.id}`}>
                        <CreditCard className="w-3.5 h-3.5 mr-2 text-sky-400" /> Převodem
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <button onClick={() => sendEmail(inv.id)} disabled={busy === "e" + inv.id} className="text-zinc-500 hover:text-emerald-400 p-1" title="Odeslat e-mailem" data-testid={`invoice-email-${inv.id}`}>
                  {busy === "e" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                </button>
                <button onClick={() => refresh(inv.id)} disabled={busy === "r" + inv.id} className="text-zinc-500 hover:text-indigo-400 p-1" title="Aktualizovat stav" data-testid={`invoice-refresh-${inv.id}`}>
                  {busy === "r" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
                <button onClick={() => setEditingId(editingId === inv.id ? null : inv.id)} className="text-zinc-500 hover:text-indigo-300 p-1" title="Upravit fakturu" data-testid={`invoice-edit-${inv.id}`}>
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={() => duplicate(inv.id)} disabled={busy === "c" + inv.id} className="text-zinc-500 hover:text-amber-300 p-1" title="Vytvořit kopii faktury (nové číslo) – např. pro vystavení na jiné IČO" data-testid={`invoice-duplicate-${inv.id}`}>
                  {busy === "c" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                </button>
                <button onClick={() => remove(inv.id)} disabled={busy === "d" + inv.id} className="text-zinc-500 hover:text-red-400 p-1" title="Smazat fakturu" data-testid={`invoice-delete-${inv.id}`}>
                  {busy === "d" + inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                </button>
                {editingId === inv.id && (
                  <EditInvoiceForm invoice={inv} token={token} onChange={onChange} onClose={() => setEditingId(null)} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvoiceSection;
